import React, { useEffect, useState } from 'react';
import GeoPosition from '../../atoms/GeoPosition';
import PlaylistAddCheck from '../../atoms/Icons/PlaylistAddCheck';
import Map from '../../atoms/Icons/Map';
import { useSelector } from 'react-redux';
import { checkIsIsoCodeProvinceHaveBranch, convertStringToSlug } from '../../../helpers/utils';
import { parameterService } from '../../../services';
import { DEFAULT_LOCATION } from 'ui/constants/defaultLocation';
import { useDispatch } from 'react-redux';
import { setShowLocationModal } from '../../../store/locationModal/actions';
import BodyLargeSemibold from '../../atoms/Text/BodyLargeSemibold';
import { IRootState } from '../../../store';
import { encode } from 'querystring';
import { useRouter } from '../../../hooks/useCustomRouter';
import useDefaultProvince from '../../../hooks/useDefaultProvince';
import { updateLocation } from '../../../services/geolocation';
import { setGeoLocationValue } from '../../../store/geolocation/actions';
import { FEATURE, PAGE_NAME_BRANCH_SALES } from '../../../constants/emarsys/branch-sales';
import { IBreadcrumbs } from '../Breadcrumbs';
import eventClickDetailBranchSales from '../../../helpers/emarsys/branch-sales/eventClickDetailBranchSales';
import { Province } from '../../../interface';

interface BranchFilterProps {
  branchCount: number;
  onClickHandlerViewMap: () => void;
  isMapView: boolean;
  onClickFilter: () => void;
  handleClickInquiry: () => void;
  city: string;
  branchProvince: Province;
  pageTitle?: string;
  breadcrumbItems?: IBreadcrumbs[];
}

const BranchFilter = ({
  branchCount,
  onClickHandlerViewMap,
  isMapView,
  onClickFilter,
  handleClickInquiry,
  city,
  branchProvince,
  pageTitle = '',
  breadcrumbItems,
}: BranchFilterProps) => {
  const chooseLocationImpressionRef = React.useRef(null);
  const filterDekstopImpressionRef = React.useRef(null);
  const filterMobileImpressionRef = React.useRef(null);
  const dispatch = useDispatch();

  const { saveDefaultCity, saveDefaultProvince } = useDefaultProvince();
  const { isocode } = useSelector((state: IRootState) => state.geolocation);
  const { userData } = useSelector((state: IRootState) => state.auth) || {
    userData: null,
  };
  const [selectedProvince, setSelectedProvince] = useState<{
    name?: string;
    isocode?: string;
  }>({});
  const [dataCityList, setDataCityList] = useState([]);
  const router = useRouter();
  const isSpecificCityPage = !!router?.query?.area;

  const eventClickEmarsys = (buttonName, sectionName, additionalNewParams) => {
    eventClickDetailBranchSales({
      dispatch,
      customerNo: userData?.customerNo,
      breadcrumbItems: breadcrumbItems,
      pageName: PAGE_NAME_BRANCH_SALES.LIST_DEALER_TOYOTA,
      pageTitle: pageTitle,
      sectionName: sectionName,
      buttonName: buttonName,
      newParams: {
        province: branchProvince?.name ?? '',
        province_code: branchProvince?.isocode ?? '',
        ...additionalNewParams,
        ...(breadcrumbItems?.[2]?.name
          ? {
              menu_cat_lvl2: breadcrumbItems?.[2]?.name?.toUpperCase(),
            }
          : {}),
      },
    });
  };

  const handleChangeLocation = async (e) => {
    const selectedDropdownCity = dataCityList.find((city) => city.label === e);

    const citySlug = convertStringToSlug(selectedDropdownCity.label);

    // Set Localstorage province_iso
    localStorage.setItem('province_iso', selectedProvince.isocode);
    localStorage.setItem('city_iso', selectedDropdownCity.value);
    localStorage.setItem('city_name', selectedDropdownCity.label);

    // Event Click Emarsys Choose Location
    eventClickEmarsys('pilih kota', 'header', {
      city: selectedDropdownCity.label ?? '',
      city_code: selectedDropdownCity.value ?? '',
    });

    // Save province to session storage if no province value from branch page
    saveDefaultProvince({
      provinceCode: selectedProvince.isocode,
      fromBranchPage: false,
    });

    // Save city to session storage if no city value from branch page
    saveDefaultCity({
      cityCode: selectedDropdownCity.value,
      fromBranchPage: false,
    });

    await updateLocation({
      city_name: selectedDropdownCity.label,
      city_iso: selectedDropdownCity.value,
    });

    dispatch(
      setGeoLocationValue({
        province: selectedProvince.name,
        isocode: selectedProvince.isocode,
        cityIso: selectedDropdownCity.value,
        city: selectedDropdownCity.label,
        allCity: [...dataCityList],
      })
    );

    window.location.href = window.location.origin + `/dealer-toyota/${citySlug}`;
  };

  useEffect(() => {
    const getProvinceHaveBranchOnly = async () => {
      const currentProvinceIsoCode = localStorage.getItem('province_iso') || isocode;
      const { isProvinceHaveBranch, data: currentProvinceDetail } =
        await checkIsIsoCodeProvinceHaveBranch(currentProvinceIsoCode);

      if (isProvinceHaveBranch) {
        setSelectedProvince(currentProvinceDetail);
      } else {
        setSelectedProvince({
          isocode: DEFAULT_LOCATION.provinceIsocode,
          name: DEFAULT_LOCATION.provinceName,
        });
      }
    };

    if (isSpecificCityPage) {
      setSelectedProvince(branchProvince);
    } else {
      getProvinceHaveBranchOnly();
    }
  }, [city, branchProvince]);

  useEffect(() => {
    if (!selectedProvince?.isocode) return;
    cityList(selectedProvince?.isocode);
  }, [selectedProvince]);

  const cityList = async (isocode) => {
    const city = [];
    const params = new URLSearchParams(
      encode({
        isOnlyHaveBranch: true,
      })
    );
    const data = await parameterService.getListCity(isocode, params);

    if (data) {
      data?.data?.map((items) => {
        city.push({
          label: items.name,
          value: items.isocode,
        });
      });
      setDataCityList(city);
    }
  };

  const handleClickProvince = () => {
    dispatch(
      setShowLocationModal({
        showModal: true,
        shouldCheckCityBranch: true,
        feature: FEATURE.BRANCH_LISTING_PAGE,
      })
    );
  };

  return (
    <section className="bg-reliableBlack3 md:bg-white">
      <div className="flex flex-col items-center relative justify-between md:flex-row lg:container xl:px-0 md:bg-reliableBlack3 lg:bg-transparent">
        {/* Desktop Resolution */}
        <div className="md:gap-4 lg:gap-8 hidden lg:flex" ref={chooseLocationImpressionRef}>
          <button className="flex items-center gap-2" onClick={handleClickProvince}>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="28"
              height="28"
              fill="none"
              viewBox="0 0 28 28"
            >
              <path
                fill="#494949"
                d="M14 19.562c1.964-1.577 3.456-3.109 4.477-4.596 1.02-1.488 1.531-2.901 1.531-4.24 0-1.13-.204-2.094-.613-2.89-.408-.797-.915-1.442-1.519-1.936a5.919 5.919 0 00-1.952-1.074c-.698-.223-1.335-.334-1.913-.334-.578 0-1.217.111-1.918.334a5.95 5.95 0 00-1.96 1.074c-.607.494-1.116 1.139-1.526 1.936-.41.796-.615 1.76-.615 2.89 0 1.339.51 2.752 1.53 4.24 1.022 1.487 2.514 3.02 4.478 4.596zm0 2.362c-2.664-1.989-4.642-3.907-5.935-5.752-1.293-1.846-1.94-3.656-1.94-5.43 0-1.34.234-2.515.701-3.524.468-1.01 1.081-1.854 1.841-2.531a7.971 7.971 0 012.547-1.54A7.957 7.957 0 0114 2.626c.939 0 1.872.174 2.8.523a8.012 8.012 0 012.533 1.539c.76.677 1.373 1.521 1.84 2.53.468 1.01.702 2.185.702 3.525 0 1.774-.647 3.584-1.94 5.43-1.293 1.845-3.271 3.763-5.935 5.752zm.004-9.266c.584 0 1.079-.204 1.486-.614.406-.409.61-.905.61-1.49 0-.583-.205-1.079-.614-1.485a2.034 2.034 0 00-1.49-.61c-.584 0-1.079.204-1.486.613-.407.41-.61.906-.61 1.49s.205 1.08.614 1.486c.41.407.906.61 1.49.61zM6.125 25.375v-1.867h15.75v1.867H6.125z"
              ></path>
            </svg>
            <BodyLargeSemibold className="uppercase">{branchProvince?.name}</BodyLargeSemibold>
          </button>
          <GeoPosition
            withIcon={false}
            value={city}
            large
            options={dataCityList && dataCityList.map((city) => city.label)}
            onChange={handleChangeLocation}
            classNameContainer="w-72"
            withChevron
          />
        </div>

        <div className="hidden lg:flex">
          <button
            ref={filterDekstopImpressionRef}
            id="button-filter-plp"
            aria-label="filter button"
            className="flex bg-reliableBlack3 mr-2 h-[52px] px-3 items-center gap-2 cursor-pointer"
            onClick={onClickFilter}
          >
            <p className="text-reliableBlack text-sm font-medium">FILTER</p>
            <PlaylistAddCheck size={24} fill="#4F4C4D" />
          </button>
          <button
            id={!isMapView ? 'map-view' : 'list-view'}
            aria-label={!isMapView ? 'map-view button' : 'list-view button'}
            className="flex bg-reliableBlack3 h-[52px] px-3 items-center  gap-2 cursor-pointer"
            onClick={() => onClickHandlerViewMap()}
          >
            <p className="text-reliableBlack text-sm font-medium">
              {!isMapView ? 'MAP VIEW' : 'LIST VIEW'}
            </p>
            <Map size={24} fill="#4F4C4D" />
          </button>
          <button
            id="ask-trade-plp"
            onClick={handleClickInquiry}
            className="lg:ml-5 px-6 h-[52px] bg-supportiveRed text-white rounded font-bold"
            aria-label="request a quote"
          >
            MINTA PENAWARAN
          </button>
        </div>

        {/* Mobile Resolution */}
        <div className="bg-reliableBlack3 flex items-center justify-between gap-6 py-4 w-full lg:hidden text-reliableBlack px-3 md:container">
          <p className="text-sm">
            {branchCount === 0 ? (
              'Cabang Tidak Ditemukan'
            ) : (
              <>
                <b className="font-semibold">{branchCount}</b> Cabang
              </>
            )}
          </p>
          <div className="flex gap-5 cursor-pointer">
            <button
              className="flex gap-2 cursor-pointer items-center"
              onClick={onClickFilter}
              aria-label="filter button"
              ref={filterMobileImpressionRef}
            >
              <p className="text-sm text-black font-medium">Filter</p>
              <PlaylistAddCheck size={24} fill="black" />
            </button>
            <button
              className="flex gap-2 cursor-pointer items-center"
              onClick={() => onClickHandlerViewMap()}
              aria-label="map view button"
            >
              <p className="text-xs font-medium">MAP</p>
              <Map size={24} fill="black" />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default BranchFilter;
